import { defineStore } from 'pinia'
import { ref, reactive, computed, watch } from 'vue'
import { store } from '@remote/store'
import { checkState } from '@remote/store/utils'
import { getDisks } from './disk'
import global from './../constantStore'
import { safeJSONParse } from '@shared/utils'
import { fileExit } from '@remote/api/adapter/shell-mqtt'
import { useDisk } from './disk'
import { getFileApi } from './index'
import type { IDiskPartitionItem, INetDrives } from '@remote/type/hezi'

const defaultSelectedPath = {
  id: '',
  path: '',
  displayPath: '内置磁盘 > 我的下载',
  diskName: '内置磁盘',
  size: -1,
  free: -1,
}

type TSelectedPath = {
  id: string
  path: string
  displayPath: string
  diskName: string
  size: number
  free: number
}

const localCacheKeyPrefix = 'HEZI_DOWNLOAD_PATH'

const setLocalCachePath = (path: TSelectedPath) => {
  const localCacheKey = `${localCacheKeyPrefix}_${global.fromSpace}`
  localStorage.setItem(localCacheKey, JSON.stringify(path))
}

const getLocalCachePath = (): TSelectedPath => {
  if (!global.fromSpace) return defaultSelectedPath
  const localCacheKey = `${localCacheKeyPrefix}_${global.fromSpace}`
  const path = localStorage.getItem(localCacheKey)
  return path ? safeJSONParse(path) as TSelectedPath : defaultSelectedPath
}

let onSetSelectedPathCallback: ((path:string) => void)|null = null

export const usePathSelector = defineStore('pathSelector', () => {
  const visibleSelectFolder = reactive({ value: false })
  const selectedPath = reactive<{ value: TSelectedPath }>({ value: getLocalCachePath() })
  const pathSelectorTitle = ref('')

  const toggleVisibleSelectFolder = (visible: boolean, title = '', callback?: (path:string) => void) => {
    console.log('toggleVisibleSelectFolder', title)
    visibleSelectFolder.value = visible
    onSetSelectedPathCallback = callback || null
    pathSelectorTitle.value = title
  }

  const setSelectedPath = (pathInfo: Omit<TSelectedPath, 'displayPath'>) => {
    const { id, path, size, free, diskName } = pathInfo
    const displayPath = path.replace(/^(\/)?(xos_storage|xos_drive)\/v1\/[\w, -]+\/?/, '').replace('/', ' > ')
    const nextPath = {
      path,
      id,
      displayPath: `${diskName}${displayPath ? ' > ' + displayPath : ''}`,
      size,
      free,
      diskName,
    }
    selectedPath.value = nextPath
    setLocalCachePath(nextPath)
    if (onSetSelectedPathCallback) {
      onSetSelectedPathCallback(path)
      onSetSelectedPathCallback = null
    }
  }
  const diskStore = useDisk()
  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })
  const diskChange = computed(() => {
    return diskStore.data.diskChange
  })

  watch(target, async (val) => {
    if (val) {
      const { root, disks, builtInDisk } = await diskStore.checkDeviceDisk()
      if (!disks.length) return
      const currentSelectedPath = getLocalCachePath()
      let nextSelectedPath = {
        id: '',
        path: `${root}/我的下载`,
        displayPath: '内置磁盘 > 我的下载',
        diskName: '内置磁盘',
        free: -1,
        size: -1,
      }
      let currentDisk

      if (currentSelectedPath.path) {
        nextSelectedPath = currentSelectedPath
        currentDisk = disks.find(item => nextSelectedPath.path.includes(item.uuid))
        console.log('currentSelectedPath', currentSelectedPath)
      } else if (builtInDisk) {
        currentDisk = builtInDisk
      } else {
        currentDisk = disks[0]
        nextSelectedPath.path = currentDisk.mount
        nextSelectedPath.displayPath = currentDisk.display_name
        nextSelectedPath.diskName = currentDisk.display_name
      }

      nextSelectedPath.free = currentDisk?.free || -1
      nextSelectedPath.size = currentDisk?.size || -1
      nextSelectedPath.id = currentDisk?.uuid || ''

      setSelectedPath(nextSelectedPath)
    }
  })
  // 检测当前路径是否存在
  const checkFileExit = async (targetPath?: string) => {
    const path = targetPath || selectedPath.value.path
    if (!path) return
    const [err, res] = await fileExit(path)
    if (!err && res?.data) {
      const data = safeJSONParse(res?.data) as { result: { exist: boolean } }
      // 查询的路径不存在
      if (!data?.result?.exist) {
        console.log('%c上次选择的路径or查询的路径不存在', path)
        const { root:buildInDiskMount, disks } = await getDisks()
        if (!disks.length) return setSelectedPath(defaultSelectedPath)
        // 自动切换至内置盘
        let currentDisk = disks.find(item => item.mount === buildInDiskMount)
        // 内置盘不存在则返回第一个外置盘
        if (!currentDisk) {
          currentDisk = disks[0]
        }
        const isBuildInDisk = currentDisk?.mount === buildInDiskMount
        const nextSelectedPath = {
          id: currentDisk?.uuid || '',
          path: isBuildInDisk ? `${buildInDiskMount}/我的下载` : currentDisk?.mount,
          displayPath: isBuildInDisk ? '内置磁盘 > 我的下载' : currentDisk?.display_name,
          diskName: currentDisk?.display_name || '',
          free: currentDisk?.free || -1,
          size: currentDisk?.size || -1,
        }
        setSelectedPath(nextSelectedPath)
      }
      // 仅查询的路径则响应路径信息，查询已选的路径则更新当前路径的磁盘大小
      if (data?.result?.exist && path.includes('/xos_storage/v1')) {
        const { disks } = await getDisks()
        const currentDisk = disks.find(item => path.includes(item.mount))
        const displayPath = path.replace(/^(\/)?xos_storage\/v1\/[\w, -]+\/?/, '')
        const nextSelectedPath = {
          id: currentDisk?.uuid || '',
          path: path,
          displayPath: `${currentDisk?.display_name || ''}${displayPath ? ' > ' + displayPath : ''}`,
          diskName: currentDisk?.display_name || '',
          free: currentDisk?.free || -1,
          size: currentDisk?.size || -1,
        }

        if (!targetPath) setSelectedPath(nextSelectedPath)
        return nextSelectedPath
      }
    }
  }
  watch(diskChange, () => {
    console.log('%c磁盘变更校验当前下载路径', 'color: red')
    checkFileExit()
  })

  return {
    visibleSelectFolder,
    toggleVisibleSelectFolder,
    selectedPath,
    setSelectedPath,
    checkFileExit,
    pathSelectorTitle,
  }
})

interface IMulPathSelectorState {
  visible: boolean
  selecting: boolean
  selecteds: string[]
  isSelectAll: boolean
  diskList: IDiskPartitionItem[]
  netDrives: INetDrives[]
  currDiskType: 'unknown' | 'hard_drive' | 'net_drive'
  currDisk: IDiskPartitionItem | INetDrives | null
  currDiskName: string
  crumbs: Array<{ id: string; name: string }>
  dataStatus: 'loading' | 'error' | 'ready'
  errorType: 'exception' | 'not_found' | ''
  errorText: string
  errorHint: string
  /** 文件列表 */
  currList: any[]
  nextPageToken: string
}
const initMulSelectorState: IMulPathSelectorState = {
  visible: false,
  selecting: false,
  selecteds: [],
  isSelectAll: false,
  diskList: [],
  netDrives: [],
  currDiskType: 'unknown',
  currDisk: null,
  currDiskName: '',
  crumbs: [],
  dataStatus: 'loading',
  errorType: '',
  errorText: '',
  errorHint: '',
  currList: [],
  nextPageToken: '',
}
export const useMultiPathSelector = defineStore('multiPathSelector', () => {
  const state: IMulPathSelectorState = reactive({ ...initMulSelectorState })
  const currentPath = computed(() => {
    if (!state.crumbs.length) return ''
    return state.crumbs[state.crumbs.length - 1].id
  })
  const reqId = ref(0)

  const diskStore = useDisk()
  const target = computed(() => {
    return store.state.curDeviceRunner?.target
  })
  watch(target, (val) => {
    if (val) {
      init()
    }
  })

  function handleGetFileApiError (err, type: IMulPathSelectorState['errorType'] = 'exception', hint = '') {
    state.dataStatus = 'error'
    state.errorType = type
    state.errorText = err.error_description || err.msg || '出错了'
    state.errorHint = hint || `错误码: ${err.error_code}`
  }

  watch(() => state.currDisk, (disk) => {
    if (disk) {
      const dir = disk!.mount
      const name = state.currDiskType === 'net_drive' ? (disk as INetDrives).path : (disk as IDiskPartitionItem).display_name || ''
      state.crumbs = [{ id: dir, name }]
      state.dataStatus = 'loading'
      reqId.value += 1
      if (state.currDiskType === 'net_drive') {
        const netDisk = disk as INetDrives
        if (netDisk.status === 'errored') {
          handleGetFileApiError({ msg: netDisk.errmsg }, 'exception', '请稍后重试')
          return
        }
      }
      const exceptionText = checkState(true, true, false, true)
      if (exceptionText) {
        handleGetFileApiError({ msg: exceptionText.split('，')[0] }, 'exception', '请稍后重试')
        return
      }
      getFileApi({
        file_path: dir,
        page_size: 50,
        last_page_token: '',
        t: Date.now(),
      }, reqId.value).then(([err, response, rid]) => {
        if (rid !== reqId.value) return
        if (err) {
          handleGetFileApiError(err)
          return
        }
        const files = Array.isArray(response.result) ? response.result : []
        state.nextPageToken = response.pageToken
        state.currList = files.filter(f => f.type === 'folder')
        state.dataStatus = 'ready'
        state.errorText = ''
        state.errorType = ''
        state.errorHint = ''
      })
    }
  })

  async function init () {
    state.crumbs = []
    state.currDisk = null
    state.currDiskName = ''
    state.currDiskType = 'unknown'
    state.currList = []
    state.nextPageToken = ''
    state.selecting = false
    state.selecteds = []
    state.isSelectAll = false
    console.log('[useMultiPathSelector]init')
    const { disks, builtInDisk, netDrives } = await diskStore.checkDeviceDisk()
    // const isDEV = process.env.NODE_ENV === 'development'
    if (!disks.length && !netDrives.length) {
      state.diskList = []
      state.netDrives = []
      state.currDiskType = 'unknown'
      state.dataStatus = 'error'
      state.errorType = 'exception'
      state.errorText = '设备磁盘异常'
      return
    }
    const diskList = disks.filter(d => d.mount)
    state.diskList = diskList
    state.netDrives = netDrives
    // 只有1个磁盘
    if (diskList.length + netDrives.length === 1) {
      state.currDiskType = netDrives.length ? 'net_drive' : 'hard_drive'
      state.currDisk = builtInDisk || diskList[0] || netDrives[0]
      state.currDiskName = state.currDiskType === 'net_drive' ? netDrives[0].path : state.currDisk.display_name || ''
    } else {
      back2chooseDisk()
    }
  }

  function enterDisk (disk: IDiskPartitionItem | INetDrives) {
    if ((disk as INetDrives).url) {
      const netDisk = disk as INetDrives
      // 网络磁盘
      if (!state.netDrives.some(d => d.url === netDisk.url)) return
      state.currDiskType = 'net_drive'
      state.currDiskName = netDisk.path
      state.currDisk = netDisk
    } else {
      const hardDisk = disk as IDiskPartitionItem
      if (!state.diskList.some(d => d.uuid === hardDisk.uuid)) return
      state.currDiskType = 'hard_drive'
      state.currDiskName = hardDisk.display_name || ''
      state.currDisk = disk
    }
  }

  function switchCrumbs (crumb: { id: string; name: string }) {
    // 选择态不处理
    if (state.selecting) return
    const targetIndex = state.crumbs.findIndex(c => c.id === crumb.id)
    if (targetIndex === -1) return
    // 当前文件夹不处理
    if (targetIndex === state.crumbs.length - 1) return
    const dir = crumb.id
    state.crumbs = state.crumbs.slice(0, targetIndex + 1)
    const exceptionText = checkState(true, true, false, true)
    if (exceptionText) {
      handleGetFileApiError({ msg: exceptionText.split('，')[0] }, 'exception', '请稍后重试')
      return
    }
    state.dataStatus = 'loading'
    reqId.value += 1
    getFileApi({
      file_path: dir,
      page_size: 50,
      last_page_token: '',
      t: Date.now(),
    }, reqId.value).then(([err, response, rid]) => {
      if (rid !== reqId.value) return
      if (err) {
        handleGetFileApiError(err)
        return
      }
      const files = Array.isArray(response.result) ? response.result : []
      state.nextPageToken = response.pageToken
      state.currList = files.filter(f => f.type === 'folder')
      state.dataStatus = 'ready'
      state.errorText = ''
      state.errorType = ''
      state.errorHint = ''
    })
  }

  async function loadNext () {
    if (!state.nextPageToken) return []
    reqId.value += 1
    return getFileApi({
      file_path: currentPath.value,
      page_size: 20,
      last_page_token: state.nextPageToken,
      t: Date.now(),
    }, reqId.value).then(([err, response, rid]) => {
      if (rid !== reqId.value) return []
      if (err) {
        const msg = err.error_description || err.msg || '出错了'
        throw new Error(msg)
      }
      const files = Array.isArray(response.result) ? response.result : []
      state.nextPageToken = response.pageToken
      state.currList = state.currList.concat(files.filter(f => f.type === 'folder'))
    })
  }

  async function enterFolder (folder: { path: string; name: string }) {
    const { path, name } = folder
    state.crumbs.push({ id: path, name })
    const exceptionText = checkState(true, true, false, true)
    if (exceptionText) {
      handleGetFileApiError({ msg: exceptionText.split('，')[0] }, 'exception', '请稍后重试')
      return
    }
    state.dataStatus = 'loading'
    reqId.value += 1
    getFileApi({
      file_path: path,
      page_size: 50,
      last_page_token: '',
      t: Date.now(),
    }, reqId.value).then(([err, response, rid]) => {
      if (rid !== reqId.value) return
      if (err) {
        handleGetFileApiError(err)
        return
      }
      const files = Array.isArray(response.result) ? response.result : []
      state.nextPageToken = response.pageToken
      state.currList = files.filter(f => f.type === 'folder')
      state.dataStatus = 'ready'
      state.errorText = ''
      state.errorType = ''
      state.errorHint = ''
    })
  }

  function setSelectState (isSelecting: boolean) {
    state.selecting = isSelecting
    if (!isSelecting) {
      state.selecteds = []
      state.selecting = false
      state.isSelectAll = false
    }
  }
  function toggleSelectItem (folder: { path: string; name: string }) {
    const { path } = folder
    if (state.selecteds.includes(path)) {
      state.selecteds = state.selecteds.filter(p => p !== path)
    } else {
      state.selecteds.push(path)
    }
    if (state.selecteds.length === 0) {
      state.selecting = false
      state.isSelectAll = false
    } else {
      state.selecting = true
      state.isSelectAll = state.selecteds.length == state.currList.length
    }
  }
  function selectAll () {
    state.selecteds = state.currList.map(f => f.path)
    state.isSelectAll = true
    state.selecting = true
  }
  function selectNone () {
    state.selecteds = []
    state.isSelectAll = false
    state.selecting = false
  }

  function back () {
    if (state.crumbs.length > 1) {
      const crumb = state.crumbs[state.crumbs.length - 2]
      switchCrumbs(crumb)
      return true
    } else if (state.crumbs.length === 1) {
      state.crumbs = []
      state.currDisk = null
      state.currDiskName = ''
      state.currDiskType = 'unknown'
      state.currList = []
      state.nextPageToken = ''
      return true
    } else {
      return false
    }
  }

  // 回退至磁盘选择列表页
  function back2chooseDisk () {
    state.crumbs = []
    state.currDisk = null
    state.currDiskName = ''
    state.currDiskType = 'unknown'
    state.currList = []
    state.nextPageToken = ''

    // 从异常状态也切换回正常状态
    state.dataStatus = 'ready'
    state.errorText = ''
    state.errorType = ''
    state.errorHint = ''
  }

  return {
    state,
    init,
    enterDisk,
    switchCrumbs,
    loadNext,
    enterFolder,
    back,
    back2chooseDisk,
    setSelectState,
    toggleSelectItem,
    selectAll,
    selectNone,
  }
})

export const checkDiskAbnormal = async (path: string) => {
  try {
    const { disks } = await getDisks()
    const currentDisk = disks.find(item => path.includes(item.uuid))
    if (!disks.length) return true
    if (currentDisk && currentDisk.free < 10 * 1024) {
      return true
    } else {
      return false
    }
  } catch (err) {
    return true
  }
}